import { useState } from 'react';
import { episodes, Episode } from '../../../mocks/episodes';

const WRENCHES = [1, 2, 3, 4, 5];

const CATEGORY_COLORS: Record<string, string> = {
  Engine: 'bg-orange-500/20 text-orange-400',
  Welding: 'bg-yellow-500/20 text-yellow-400',
  Brakes: 'bg-red-500/20 text-red-400',
  Exhaust: 'bg-amber-500/20 text-amber-400',
  'Small Engine': 'bg-green-500/20 text-green-400',
};

const ProjectLog = ({ ep }: { ep: Episode }) => (
  <div className="bg-[#1e1e1e] rounded-xl p-6 border border-[#2a2a2a] flex flex-col gap-5 h-full">
    {/* Log Header */}
    <div className="flex items-center gap-2 pb-4 border-b border-[#2a2a2a]">
      <div className="w-5 h-5 flex items-center justify-center text-[#f5a623]">
        <i className="ri-file-list-3-line" />
      </div>
      <span className="text-[#f5a623] text-xs font-['Roboto'] font-bold tracking-[4px] uppercase">
        Project Log
      </span>
      <div className="ml-auto bg-[#f5a623]/10 border border-[#f5a623]/30 rounded-md px-3 py-1">
        <span className="text-[#f5a623] text-xs font-['Roboto'] font-bold tracking-[1px]">
          EP. {String(ep.number).padStart(2, '0')}
        </span>
      </div>
    </div>

    {/* Status */}
    <div className="flex items-center justify-between">
      <div>
        <p className="text-[#9a9a9a] text-[10px] font-['Roboto'] tracking-[2px] uppercase mb-1">Status</p>
        <div className="flex items-center gap-2">
          <span className="text-white font-['Roboto_Mono'] font-bold">{ep.status}</span>
          <span className="text-green-400">&#10003;</span>
        </div>
      </div>
      <span
        className={`text-[10px] font-['Roboto'] font-bold px-2 py-1 rounded-full ${CATEGORY_COLORS[ep.category] ?? 'bg-[#f5a623]/20 text-[#f5a623]'}`}
      >
        {ep.category}
      </span>
    </div>

    {/* Beer Reward */}
    <div>
      <p className="text-[#9a9a9a] text-[10px] font-['Roboto'] tracking-[2px] uppercase mb-2">The Reward</p>
      <div className="flex items-start gap-2">
        <div className="w-5 h-5 flex items-center justify-center text-[#f5a623] mt-0.5 shrink-0">
          <i className="ri-goblet-fill" />
        </div>
        <div>
          <span className="text-[#f5a623] font-['Roboto'] font-bold text-sm leading-tight">{ep.beer}</span>
          <p className="text-[#6a6a6a] text-xs font-['Roboto'] mt-0.5">{ep.beerBrewery}</p>
        </div>
      </div>
    </div>

    {/* Difficulty */}
    <div>
      <p className="text-[#9a9a9a] text-[10px] font-['Roboto'] tracking-[2px] uppercase mb-2">Difficulty</p>
      <div className="flex items-center gap-1.5">
        {WRENCHES.map((w) => (
          <div key={w} className="w-5 h-5 flex items-center justify-center">
            <i className={`ri-tools-fill text-sm ${w <= ep.difficulty ? 'text-[#f5a623]' : 'text-[#333333]'}`} />
          </div>
        ))}
        <span className="text-[#9a9a9a] text-xs font-['Roboto_Mono'] ml-1">{ep.difficulty}/5</span>
      </div>
    </div>

    {/* Time & Cost */}
    <div className="grid grid-cols-2 gap-4">
      <div>
        <p className="text-[#9a9a9a] text-[10px] font-['Roboto'] tracking-[2px] uppercase mb-1">Time Spent</p>
        <span className="text-white font-['Roboto_Mono'] font-bold text-sm">{ep.timeSpent}</span>
      </div>
      <div>
        <p className="text-[#9a9a9a] text-[10px] font-['Roboto'] tracking-[2px] uppercase mb-1">Total Cost</p>
        <span className="text-white font-['Roboto_Mono'] font-bold text-sm">{ep.cost}</span>
      </div>
    </div>

    {/* Verdict */}
    <div className="bg-[#111111] rounded-lg p-4 border-l-2 border-[#f5a623] flex-1">
      <p className="text-[#c4c4c4] text-xs font-['Roboto'] italic leading-relaxed">
        &ldquo;{ep.verdict}&rdquo;
      </p>
    </div>

    {/* CTA */}
    <a
      href="https://www.youtube.com/@beerafterwork"
      target="_blank"
      rel="nofollow noopener noreferrer"
      className="flex items-center justify-center gap-2 border border-[#f5a623]/40 hover:bg-[#f5a623] hover:border-[#f5a623] text-[#f5a623] hover:text-black font-['Roboto'] font-bold text-xs tracking-[1px] uppercase py-3 rounded-md transition-all duration-200 cursor-pointer whitespace-nowrap"
    >
      Watch Full Episode
      <div className="w-4 h-4 flex items-center justify-center">
        <i className="ri-arrow-right-line" />
      </div>
    </a>
  </div>
);

const EpisodesSection = () => {
  const [featured, setFeatured] = useState<Episode>(episodes[0]);
  const [transitioning, setTransitioning] = useState(false);

  const handleSelect = (ep: Episode) => {
    if (ep.id === featured.id) return;
    setTransitioning(true);
    setTimeout(() => {
      setFeatured(ep);
      setTransitioning(false);
    }, 180);
  };

  return (
    <section id="latest-biscuit" className="w-full bg-[#1a1a1a] py-24">
      <div className="max-w-7xl mx-auto px-6">

        {/* Section Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-12">
          <div className="flex items-center gap-4">
            <div className="w-1 h-8 bg-[#f5a623] rounded-full" />
            <div>
              <p className="text-[#f5a623] text-xs font-['Roboto'] font-bold tracking-[4px] uppercase mb-1">
                Episode Archive
              </p>
              <h2 className="font-['Bebas_Neue'] text-5xl text-white tracking-wider">
                THE LATEST BISCUIT
              </h2>
            </div>
          </div>
          <p className="text-[#9a9a9a] text-sm font-['Roboto'] max-w-sm leading-relaxed">
            Every fix documented. Every beer earned. Click any episode to load the project log.
          </p>
        </div>

        {/* Top Grid: Video + Project Log */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-stretch mb-6">
          {/* Featured Video */}
          <div className="lg:col-span-2 flex flex-col gap-4">
            <div
              className={`relative w-full rounded-xl overflow-hidden border transition-all duration-200 ${
                transitioning ? 'opacity-0 scale-[0.99] border-[#f5a623]/10' : 'opacity-100 scale-100 border-[#f5a623]/30'
              }`}
              style={{ aspectRatio: '16/9', boxShadow: '0 16px 60px rgba(0,0,0,0.8)' }}
            >
              <iframe
                key={featured.id}
                className="absolute inset-0 w-full h-full"
                src={`https://www.youtube.com/embed/${featured.videoId}?rel=0&modestbranding=1`}
                title={`Beer After Work – EP.${featured.number} – ${featured.title}`}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>

            {/* Episode Info Bar */}
            <div
              className={`bg-[#1e1e1e] rounded-xl px-5 py-4 border border-[#2a2a2a] flex flex-col sm:flex-row sm:items-center justify-between gap-3 transition-opacity duration-200 ${transitioning ? 'opacity-0' : 'opacity-100'}`}
            >
              <div>
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className="text-[#f5a623] text-xs font-['Roboto_Mono'] font-bold">
                    EP. {String(featured.number).padStart(2, '0')}
                  </span>
                  <span className="text-[#6a6a6a] text-xs">·</span>
                  <span className="text-[#9a9a9a] text-xs font-['Roboto']">{featured.date}</span>
                  <span className="text-[#6a6a6a] text-xs">·</span>
                  <span className="text-[#9a9a9a] text-xs font-['Roboto']">{featured.duration}</span>
                </div>
                <h3 className="text-white font-['Roboto'] font-bold text-base leading-tight">
                  {featured.title}
                </h3>
              </div>
              <a
                href="https://www.youtube.com/@beerafterwork"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="flex items-center gap-2 text-[#f5a623] hover:text-[#d4722e] text-xs font-bold font-['Roboto'] transition-colors cursor-pointer whitespace-nowrap"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-youtube-fill" />
                </div>
                Watch on YouTube
              </a>
            </div>
          </div>

          {/* Project Log */}
          <div
            className={`lg:col-span-1 transition-opacity duration-200 ${transitioning ? 'opacity-0' : 'opacity-100'}`}
          >
            <ProjectLog ep={featured} />
          </div>
        </div>

        {/* Episode Browser Row */}
        <div className="flex items-stretch gap-4 overflow-x-auto pb-3" style={{ scrollbarWidth: 'thin', scrollbarColor: '#f5a623 #1a1a1a' }}>
          {episodes.map((ep) => {
            const isActive = ep.id === featured.id;
            return (
              <button
                key={ep.id}
                onClick={() => handleSelect(ep)}
                className={`group shrink-0 w-[200px] flex flex-col rounded-xl overflow-hidden border transition-all duration-200 cursor-pointer ${
                  isActive
                    ? 'border-[#f5a623]/60 bg-[#f5a623]/5'
                    : 'border-[#2a2a2a] bg-[#1e1e1e] hover:border-[#f5a623]/30 hover:bg-[#242424]'
                }`}
              >
                {/* Thumbnail */}
                <div className="relative w-full h-[112px] overflow-hidden bg-[#111111]">
                  <img
                    src={ep.thumbnail}
                    alt={ep.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className={`absolute inset-0 flex items-center justify-center transition-opacity duration-200 ${isActive ? 'bg-[#f5a623]/15' : 'bg-black/50 group-hover:bg-black/30'}`}>
                    <div className={`w-8 h-8 flex items-center justify-center rounded-full transition-all duration-200 ${isActive ? 'bg-[#f5a623]' : 'bg-black/60 group-hover:bg-[#f5a623]/80'}`}>
                      <i className={`ri-play-fill text-sm ${isActive ? 'text-black' : 'text-white'}`} />
                    </div>
                  </div>
                  <span className="absolute bottom-1.5 right-1.5 bg-black/80 text-white text-[10px] font-['Roboto_Mono'] px-1.5 py-0.5 rounded">
                    {ep.duration}
                  </span>
                </div>

                {/* Card Info */}
                <div className="p-3 flex flex-col gap-1.5 text-left">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className={`text-[10px] font-['Roboto_Mono'] font-bold ${isActive ? 'text-[#f5a623]' : 'text-[#6a6a6a]'}`}>
                      EP.{String(ep.number).padStart(2, '0')}
                    </span>
                    <span className={`text-[9px] font-['Roboto'] font-bold px-1.5 py-0.5 rounded-full ${CATEGORY_COLORS[ep.category] ?? 'bg-[#f5a623]/20 text-[#f5a623]'}`}>
                      {ep.category}
                    </span>
                  </div>
                  <p className={`text-xs font-['Roboto'] font-bold leading-snug line-clamp-2 text-left ${isActive ? 'text-white' : 'text-[#c4c4c4] group-hover:text-white'} transition-colors`}>
                    {ep.title}
                  </p>
                  <div className="flex items-center gap-1 mt-0.5">
                    {WRENCHES.map((w) => (
                      <div key={w} className="w-3 h-3 flex items-center justify-center">
                        <i className={`ri-tools-fill text-[9px] ${w <= ep.difficulty ? 'text-[#f5a623]' : 'text-[#333333]'}`} />
                      </div>
                    ))}
                  </div>
                </div>
              </button>
            );
          })}

          {/* View All card */}
          <a
            href="https://www.youtube.com/@beerafterwork/videos"
            target="_blank"
            rel="nofollow noopener noreferrer"
            className="shrink-0 w-[160px] flex flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-[#333333] hover:border-[#f5a623]/50 bg-[#1e1e1e] hover:bg-[#242424] transition-all duration-200 cursor-pointer p-6"
          >
            <div className="w-10 h-10 flex items-center justify-center rounded-full border border-[#333333] text-[#6a6a6a]">
              <i className="ri-youtube-line text-lg" />
            </div>
            <span className="text-[#9a9a9a] hover:text-[#f5a623] text-xs font-['Roboto'] font-bold tracking-[1px] uppercase text-center leading-snug transition-colors">
              See All Episodes
            </span>
          </a>
        </div>

      </div>
    </section>
  );
};

export default EpisodesSection;
